<script lang="ts">
  import {
    goto,
    Router,
    type RouteConfig,
  } from '@mateothegreat/svelte5-router'
  import { QueryClientProvider } from '@tanstack/svelte-query'
  import queryClient from './constants/query-client'
  import { RenderScan } from 'svelte-render-scan'
  import { getCurrentUser, signIn } from './api'
  import { SvelteQueryDevtools } from '@tanstack/svelte-query-devtools'
  import {
    goToAuthPortal,
    getStoredCodeVerifier,
    getStoredState,
    getStoredNonce,
    clearOauthState,
  } from './helpers/oauth'
  import { persistUserLocale } from './helpers/locale'
  import { captureException } from '@sentry/svelte'
  import { authChecking } from './stores/auth-transition'
  import TestEnvBanner from './components/test-env-banner/TestEnvBanner.svelte'
  import FeatureFlagsDevtools from './components/feature-flags-devtools/FeatureFlagsDevtools.svelte'
  import AuthLoadingScreen from './components/auth-loading-screen/AuthLoadingScreen.svelte'
  import SignIn from './routes/sign-in/SignIn.svelte'
  import CookiePolicy from './routes/cookie-policy/CookiePolicy.svelte'
  import PrivacyPolicy from './routes/privacy-policy/PrivacyPolicy.svelte'
  import TermsOfUse from './routes/terms-of-use/TermsOfUse.svelte'
  import Projects from './routes/projects/Projects.svelte'
  import Project from './routes/project/Project.svelte'
  import ProjectPreview from './routes/project-preview/ProjectPreview.svelte'

  // Guards against the auth hook firing twice before the session cookie is set:
  // the second run would find the single-use PKCE material already cleared and
  // pointlessly restart the whole authorize flow (a redirect loop that can burn
  // the entire test/navigation budget on CI).
  let codeExchangeInFlight = false

  // Hides the auth loading screen once a route has actually rendered. Attached
  // per-route (not as a global `hooks` prop on <Router>) because
  // RouterInstanceConfig merges `{ ...route, ...config }` per route — a global
  // `hooks` prop there overwrites each route's own `hooks.pre`, silently
  // deleting the auth guard.
  const markAuthResolved = () => authChecking.set(false)

  const getOauthCode = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search)
      const code = urlParams.get('code') as string
      const returnedState = urlParams.get('state')

      if (code) {
        if (codeExchangeInFlight) return false
        codeExchangeInFlight = true

        const codeVerifier = getStoredCodeVerifier()
        const storedState = getStoredState()
        const nonce = getStoredNonce()

        // Restart the flow if PKCE/CSRF material is missing or the state does
        // not match what we issued (possible CSRF / replay).
        if (
          !codeVerifier
          || !nonce
          || !storedState
          || storedState !== returnedState
        ) {
          clearOauthState()
          await goToAuthPortal()
          return false
        }
        await signIn({ code, codeVerifier, nonce })
        // Only clear the single-use material once the exchange has succeeded.
        clearOauthState()
        const { data: user } = await getCurrentUser()
        await persistUserLocale(user)
        goto('/projects')
        return true
      }
      else {
        await goToAuthPortal()
      }
    }
    catch (err) {
      captureException(err)
      // Allow a retry on transient failure instead of wedging the flow.
      codeExchangeInFlight = false
    }

    return false
  }

  const globalAuthGuardHook = async (): Promise<boolean> => {
    try {
      const { data: user } = await getCurrentUser()
      await persistUserLocale(user)
      return true
    }
    catch (err) {
      console.error(err)
      await getOauthCode()
      return false
    }
  }

  const routes: RouteConfig[] = [
    {
      path: '',
      hooks: {
        pre: async () => {
          try {
            const result = await globalAuthGuardHook()

            if (result) {
              goto('/projects')
            }
          }
          catch (err) {
            console.error(err)
            await goToAuthPortal()
          }
        },
      },
    },
    {
      path: 'sign-in',
      component: SignIn,
      hooks: {
        post: markAuthResolved,
      },
    },
    {
      path: 'cookie-policy',
      component: CookiePolicy,
      hooks: {
        post: markAuthResolved,
      },
    },
    {
      path: 'privacy-policy',
      component: PrivacyPolicy,
      hooks: {
        post: markAuthResolved,
      },
    },
    {
      path: 'terms-of-use',
      component: TermsOfUse,
      hooks: {
        post: markAuthResolved,
      },
    },
    {
      path: 'projects',
      component: Projects,
      hooks: {
        pre: globalAuthGuardHook,
        post: markAuthResolved,
      },
    },
    {
      path: 'projects/(?<id>.*)',
      component: Project,
      hooks: {
        pre: globalAuthGuardHook,
        post: markAuthResolved,
      },
    },
    {
      path: 'projects/(?<id>.*)/preview',
      component: ProjectPreview,
      hooks: {
        pre: globalAuthGuardHook,
        post: markAuthResolved,
      },
    },
  ]
</script>

{#if !import.meta.env.PROD}
  <RenderScan />
{/if}
<main class="container">
  <TestEnvBanner />
  <QueryClientProvider client={queryClient}>
    {#if !import.meta.env.PROD}
      <SvelteQueryDevtools />
    {/if}
    <Router {routes} />
  </QueryClientProvider>
  <FeatureFlagsDevtools />
  {#if $authChecking}
    <AuthLoadingScreen />
  {/if}
</main>

<style lang="scss">
  .container {
    background: var(--background);
    color: var(--foreground);
    min-height: 100vh;
    max-width: 100vw;
  }
</style>
